import { OptionsIcon, type DeckardIcon } from '@assets/icons';
import { IconButton, type IconButtonProps } from '@components/button';
import { ClickAwayListener, Menu, MenuItem, type MenuProps } from '@mui/material';
import {
  useState,
  type MouseEventHandler,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';

export type OptionsMenuItem = {
  label: ReactNode;
  onClick: () => void;
};

type OptionsMenuProps = Omit<Partial<IconButtonProps>, 'children'> & {
  items?: OptionsMenuItem[];
  keepMounted?: boolean;
  menuProps?: Partial<MenuProps>;
  customItems?: boolean;
  Icon?: DeckardIcon;
  children?:
    | ReactNode
    | ((onClose: (event: ReactMouseEvent | MouseEvent | TouchEvent) => void) => ReactNode);
};

export const OptionsMenu = ({
  items,
  keepMounted = true,
  menuProps,
  customItems = false,
  children,
  Icon = OptionsIcon,
  ...props
}: OptionsMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick: MouseEventHandler<HTMLElement> = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (event: MouseEvent | TouchEvent | ReactMouseEvent) => {
    event.stopPropagation();
    setAnchorEl(null);
  };
  const handleItemClick = (event: ReactMouseEvent, onClick: () => void) => {
    event.stopPropagation();
    setAnchorEl(null);
    onClick();
  };
  return (
    <>
      <IconButton Icon={Icon} size={20} aria-label="options" onClick={handleClick} {...props} />
      <ClickAwayListener onClickAway={handleClose}>
        <Menu
          anchorEl={anchorEl}
          keepMounted={keepMounted}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          elevation={0}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          {...menuProps}
        >
          {customItems
            ? typeof children === 'function'
              ? children(handleClose)
              : children
            : items &&
              items.map(({ label, onClick }, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <MenuItem key={index} onClick={(event) => handleItemClick(event, onClick)}>
                  {label}
                </MenuItem>
              ))}
        </Menu>
      </ClickAwayListener>
    </>
  );
};
